/**
 * Map UI speed multipliers (1x–4x) to scroll pixels-per-frame using
 * aspect-ratio family height baselines. Within a family, for a fixed fps
 * and speed, every resolution shares the same running time:
 *
 *   sppf = BASE_PX_AT_1X * (H / H_base) * speed
 */

export const SPEED_OPTIONS = [1, 1.5, 2, 2.5, 3, 3.5, 4];

export const DEFAULT_SPEED = 1;

/** Pixels per frame at 1x on each family's baseline height. */
const BASE_PX_AT_1X = 2;

/**
 * Each output resolution → its AR-family baseline (used for identical
 * running-time estimates across the group).
 */
const RESOLUTION_FAMILY_BASELINE = {
  // A — 1.33 (4:3)
  "1920 x 1440": { key: "1920 x 1440", width: 1920, height: 1440 },
  "3840 x 2880": { key: "1920 x 1440", width: 1920, height: 1440 },

  // B — 1.78 (16:9)
  "1280 x 720": { key: "1280 x 720", width: 1280, height: 720 },
  "1920 x 1080": { key: "1280 x 720", width: 1280, height: 720 },
  "2048 x 1152": { key: "1280 x 720", width: 1280, height: 720 },
  "3840 x 2160": { key: "1280 x 720", width: 1280, height: 720 },

  // C — 1.85 (Flat)
  "1998 x 1080": { key: "1998 x 1080", width: 1998, height: 1080 },
  "3996 x 2160": { key: "1998 x 1080", width: 1998, height: 1080 },

  // D — ~1.90 (DCI)
  "2048 x 1080": { key: "2048 x 1080", width: 2048, height: 1080 },
  "4096 x 2160": { key: "2048 x 1080", width: 2048, height: 1080 },

  // E — 2.00 (Univisium)
  "1920 x 960": { key: "1920 x 960", width: 1920, height: 960 },

  // F — 2.39 (Scope)
  "2048 x 858": { key: "2048 x 858", width: 2048, height: 858 },
  "3840 x 1608": { key: "2048 x 858", width: 2048, height: 858 },
  "4096 x 1716": { key: "2048 x 858", width: 2048, height: 858 },
};

export const speedDropdownOptions = SPEED_OPTIONS.map((speed) => ({
  label: `${speed}x`,
  value: speed,
}));

/**
 * Baseline canvas for the resolution's aspect-ratio family.
 * Running-time estimates should use this so every res in the group matches.
 */
export function getFamilyBaseline(resolutionKey, fallbackWidth, fallbackHeight) {
  const mapped = resolutionKey
    ? RESOLUTION_FAMILY_BASELINE[resolutionKey]
    : null;
  if (mapped) {
    return mapped;
  }
  return {
    key: resolutionKey || null,
    width: Number(fallbackWidth) || 0,
    height: Number(fallbackHeight) || 0,
  };
}

/**
 * Exact float px/frame for the given output size and speed multiplier.
 */
export function getSpfForSpeed({
  resolutionKey,
  videoHeight,
  speed = DEFAULT_SPEED,
}) {
  const speedMult = Number(speed);
  const height = Number(videoHeight);

  if (!speedMult || !height) {
    return BASE_PX_AT_1X;
  }

  const baseline = resolutionKey
    ? RESOLUTION_FAMILY_BASELINE[resolutionKey]
    : null;
  const hBase = baseline?.height || height;

  return BASE_PX_AT_1X * (height / hBase) * speedMult;
}

export function isValidSpeed(value) {
  const n = Number(value);
  return SPEED_OPTIONS.some((s) => s === n);
}

/**
 * Inverse of getSpfForSpeed: recovers the UI speed multiplier (1x-4x) from a
 * stored px/frame value, snapping to the nearest supported SPEED_OPTIONS
 * entry since spf is persisted as a rounded float.
 */
export function getSpeedFromSpf({ resolutionKey, videoHeight, spf }) {
  const height = Number(videoHeight);
  const value = Number(spf);

  if (!height || !value) return DEFAULT_SPEED;

  const baseline = resolutionKey ? RESOLUTION_FAMILY_BASELINE[resolutionKey] : null;
  const hBase = baseline?.height || height;
  const spfAt1x = BASE_PX_AT_1X * (height / hBase);

  if (!spfAt1x) return DEFAULT_SPEED;

  const rawSpeed = value / spfAt1x;
  return SPEED_OPTIONS.reduce(
    (closest, option) =>
      Math.abs(option - rawSpeed) < Math.abs(closest - rawSpeed) ? option : closest,
    SPEED_OPTIONS[0],
  );
}
